import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProfile } from '@/hooks/use-profile'
import { useAuthStore } from '@/stores/auth-store'
import { useThemeStore, THEMES, type Theme } from '@/stores/theme-store'
import { useSetTheme } from '@/lib/theme'
import { Avatar } from './avatar'

export function UserMenu(): React.ReactElement {
  const { profile } = useProfile()
  const userEmail = useAuthStore((s) => s.user?.email ?? null)
  const signOut = useAuthStore((s) => s.signOut)
  const navigate = useNavigate()
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useSetTheme()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const displayName = profile?.displayName ?? userEmail ?? 'You'

  const handleSignOut = async (): Promise<void> => {
    setOpen(false)
    await signOut()
    navigate('/login')
  }

  return (
    <div className="relative" ref={containerRef} data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center rounded-full ring-1 ring-border hover:ring-primary"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="user-menu-trigger"
      >
        <Avatar name={displayName} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-md border border-border bg-surface-elevated py-2 shadow-lg"
          data-testid="user-menu-panel"
        >
          <div className="px-4 py-2">
            <div className="truncate text-sm font-medium text-foreground">{displayName}</div>
            {userEmail && (
              <div className="truncate text-xs text-muted-foreground">{userEmail}</div>
            )}
          </div>
          <div className="my-1 h-px bg-border" />
          <div className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Appearance
          </div>
          {THEMES.map((option) => {
            const selected = option.value === theme
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => setTheme(option.value as Theme)}
                className={`flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-muted ${
                  selected ? 'font-medium text-primary' : 'text-foreground'
                }`}
                data-testid={`theme-option-${option.value}`}
              >
                {option.label}
              </button>
            )
          })}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="block w-full px-4 py-2 text-left text-sm text-error-base hover:bg-muted"
            data-testid="sign-out"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
